import { useQuery } from '@apollo/client';
import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { CardSeparator } from '../components/Card';
import CardHeader from '../components/CardHeader';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import useIsPublicView from '../utils/useIsPublicView';
import EditSeriesModal from './modal/EditSeriesModal';
import styles from './SeriesHoverCard.module.scss';
import SeriesIcon from './SeriesIcon';
import useSeriesPolicyCheck, {
  SeriesPolicyAction,
} from './useSeriesPolicyCheck';
import SeriesRow from './SeriesRow';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UIActions } from '../ui/uiSlice';

const SERIES_HOVER_CARD_SERIES_FRAGMENT = gql(`
  fragment SeriesHoverCard_series on PublicSeriesFields {
    id
    name
    posts {
      id
      ...SeriesRow_post
    }
    ...useSeriesPolicyCheck_series
  }
`);

const SERIES_QUERY = gql(`
  query SeriesHoverCard($id: ID!) {
    post(id: $id) {
      id
      series {
        ...SeriesHoverCard_series
      }
    }
  }
`);

const PUBLIC_SERIES_QUERY = gql(`
  query PublicSeriesHoverCard($id: ID!) {
    post: publicPost(id: $id) {
      id
      series {
        ...SeriesHoverCard_series
      }
    }
  }
`);

interface UseSeriesArgs {
  postId: string;
  isPublicView: boolean;
}

function useSeries({
  postId,
  isPublicView,
}: UseSeriesArgs): DocumentType<
  typeof SERIES_HOVER_CARD_SERIES_FRAGMENT
> | null {
  const { data } = useQuery(isPublicView ? PUBLIC_SERIES_QUERY : SERIES_QUERY, {
    variables: { id: postId },
  });

  return data?.post.series ?? null;
}

interface SeriesHoverCardProps {
  postId: string;
  showEditButton?: boolean;
}

const SeriesHoverCard = ({
  postId,
  showEditButton = false,
}: SeriesHoverCardProps) => {
  const isPublicView = useIsPublicView();
  const seriesPolicyCheck = useSeriesPolicyCheck();
  const series = useSeries({ postId, isPublicView });

  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();

  if (!series) {
    return null;
  }

  return (
    <PopOverContainer width="wide">
      <CardHeader
        icon={<SeriesIcon />}
        aboveTitle={<div className={styles.meta}>Series</div>}
        title={series.name}
        rightAction={
          !isPublicView &&
          showEditButton &&
          currentUser?.id &&
          seriesPolicyCheck(
            SeriesPolicyAction.EditSeries,
            series,
            currentUser,
          ) && (
            <Button
              variant="secondary"
              onClick={() =>
                dispatch(
                  UIActions.openModal(EditSeriesModal, { seriesId: series.id }),
                )
              }
            >
              Edit
            </Button>
          )
        }
      />
      <CardSeparator />
      {series.posts.map((post, index) => (
        <SeriesRow
          key={post.id}
          active={post.id === postId}
          index={index}
          post={post}
        />
      ))}
    </PopOverContainer>
  );
};

export default SeriesHoverCard;
